import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Loader from '../../components/common/Loader';
import { getReporteeCourseProgress, getSubtreeCourseSummary, getSubtreeUsers } from '../../services/adminService';
import '../../styles/orgChart.css';

function TeamCourseProgress() {
  const navigate = useNavigate();

  const panRef = useRef(null);
  const nodeRefs = useRef(new Map());

  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryRows, setSummaryRows] = useState([]);
  const [query, setQuery] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0, panX: 0, panY: 0 });
  const [imgFailed, setImgFailed] = useState(() => new Set());

  const [courseModalOpen, setCourseModalOpen] = useState(false);
  const [courseModalLoading, setCourseModalLoading] = useState(false);
  const [courseModalUser, setCourseModalUser] = useState(null);
  const [courseModalCourses, setCourseModalCourses] = useState([]);

  useEffect(() => {
    const fetchSummary = async () => {
      setSummaryLoading(true);
      try {
        const res = await getSubtreeCourseSummary();
        if (res?.success) {
          const rows = Array.isArray(res?.data?.rows)
            ? res.data.rows
            : Array.isArray(res?.data)
              ? res.data
              : [];
          setSummaryRows(rows);
        } else {
          toast.error(res?.message || res?.error_msg || 'Failed to load summary');
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || 'Failed to load summary');
      } finally {
        setSummaryLoading(false);
      }
    };

    fetchSummary();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      setUsersLoading(true);
      try {
        const res = await getSubtreeUsers();
        if (res?.success) {
          const list = Array.isArray(res?.data?.users) ? res.data.users : Array.isArray(res?.data) ? res.data : [];
          setUsers(list);
          const roots = list.filter((u) => {
            const supRaw = u?.supervisor_id;
            return supRaw == null || supRaw === 0 || supRaw === '0' || supRaw === '';
          });
          setExpanded(new Set(roots.map((u) => String(u.id))));
        } else {
          toast.error(res?.message || res?.error_msg || 'Failed to load team hierarchy');
          setUsers([]);
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || 'Failed to load team hierarchy');
        setUsers([]);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const progressByUserId = useMemo(() => {
    const map = new Map();
    (summaryRows || []).forEach((r) => {
      const id = r?.user_id ?? r?.id;
      if (id == null) return;
      map.set(String(id), {
        enrolled: Number(r?.enrolled_count || 0),
        completed: Number(r?.completed_count || 0),
        inProgress: Number(r?.in_progress_count || 0),
        notStarted: Number(r?.not_started_count || 0),
        completion: r?.completion_percent == null ? null : Number(r?.completion_percent),
      });
    });
    return map;
  }, [summaryRows]);

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clampZoom = (value) => Math.min(2, Math.max(0.55, value));
  const zoomIn = () => setZoom((z) => clampZoom(Number((z + 0.1).toFixed(2))));
  const zoomOut = () => setZoom((z) => clampZoom(Number((z - 0.1).toFixed(2))));
  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    setIsPanning(true);
    setPanStart({ x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y });
  };

  const onMouseMove = (e) => {
    if (!isPanning) return;
    const dx = e.clientX - panStart.x;
    const dy = e.clientY - panStart.y;
    setPan({ x: panStart.panX + dx, y: panStart.panY + dy });
  };

  const onMouseUp = () => {
    setIsPanning(false);
  };

  const onPointerDown = (e) => {
    if (e.button != null && e.button !== 0) return;
    setIsPanning(true);
    setPanStart({ x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y });
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // no-op
    }
  };

  const onPointerMove = (e) => {
    if (!isPanning) return;
    const dx = e.clientX - panStart.x;
    const dy = e.clientY - panStart.y;
    setPan({ x: panStart.panX + dx, y: panStart.panY + dy });
  };

  const onPointerUp = (e) => {
    setIsPanning(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // no-op
    }
  };

  const { childrenBySupervisorId, roots } = useMemo(() => {
    const childrenMap = new Map();
    (users || []).forEach((u) => {
      const supRaw = u?.supervisor_id;
      const isNone = supRaw == null || supRaw === 0 || supRaw === '0' || supRaw === '';
      const key = isNone ? null : String(supRaw);
      if (!childrenMap.has(key)) childrenMap.set(key, []);
      childrenMap.get(key).push(u);
    });

    for (const [, list] of childrenMap) {
      list.sort((a, b) => {
        const an = (a?.name || '').toString().toLowerCase();
        const bn = (b?.name || '').toString().toLowerCase();
        return an.localeCompare(bn);
      });
    }

    const rootList = childrenMap.get(null) || [];
    return { childrenBySupervisorId: childrenMap, roots: rootList };
  }, [users]);

  const renderProgress = (userId) => {
    const p = progressByUserId.get(String(userId));
    const enrolled = p?.enrolled ?? 0;
    const completed = p?.completed ?? 0;
    const inProgress = p?.inProgress ?? 0;
    const notStarted = p?.notStarted ?? 0;
    const denom = completed + inProgress + notStarted;
    const cPct = denom ? (completed / denom) * 100 : 0;
    const ipPct = denom ? (inProgress / denom) * 100 : 0;
    const nsPct = denom ? (notStarted / denom) * 100 : 0;

    return (
      <div className="mt-2">
        <div className="flex items-center justify-between">
          <div className="text-[11px] text-gray-500">{completed}/{enrolled} completed</div>
          <div className="text-[11px] font-black text-gray-700">
            {p?.completion == null ? '—' : `${p.completion}%`}
          </div>
        </div>
        <div className="mt-1 w-full h-2 rounded-full bg-gray-100 overflow-hidden flex">
          <div className="h-full bg-emerald-500" style={{ width: `${cPct}%` }} />
          <div className="h-full bg-amber-400" style={{ width: `${ipPct}%` }} />
          <div className="h-full bg-slate-300" style={{ width: `${nsPct}%` }} />
        </div>
      </div>
    );
  };

  const queryLower = useMemo(() => (query || '').trim().toLowerCase(), [query]);
  const isMatch = (u) => {
    if (!queryLower) return true;
    const name = (u?.name || '').toString().toLowerCase();
    const email = (u?.email || '').toString().toLowerCase();
    return name.includes(queryLower) || email.includes(queryLower);
  };

  const matchedUserIds = useMemo(() => {
    if (!queryLower) return [];
    const ids = [];
    (users || []).forEach((u) => {
      if (isMatch(u)) ids.push(String(u.id));
    });
    return ids;
  }, [users, queryLower]);

  const [activeMatchIndex, setActiveMatchIndex] = useState(0);
  useEffect(() => {
    setActiveMatchIndex(0);
  }, [queryLower]);

  const activeMatchId = matchedUserIds.length ? matchedUserIds[Math.min(activeMatchIndex, matchedUserIds.length - 1)] : null;

  const focusUserById = (id) => {
    if (!id) return;
    const el = nodeRefs.current.get(String(id));
    const panEl = panRef.current;
    if (!el || !panEl) return;

    const panRect = panEl.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const panCenterX = panRect.left + panRect.width / 2;
    const panCenterY = panRect.top + panRect.height / 2;
    const elCenterX = elRect.left + elRect.width / 2;
    const elCenterY = elRect.top + elRect.height / 2;
    const dx = panCenterX - elCenterX;
    const dy = panCenterY - elCenterY;
    setPan((prev) => ({ x: prev.x + dx / zoom, y: prev.y + dy / zoom }));
  };

  useEffect(() => {
    if (!activeMatchId) return;
    focusUserById(activeMatchId);
  }, [activeMatchId, zoom]);

  const getInitials = (name) => {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'U';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const toAbsoluteUrl = (maybeUrl) => {
    if (!maybeUrl) return null;
    const str = String(maybeUrl).trim();
    if (!str) return null;
    if (/^https?:\/\//i.test(str)) return str;

    const base = (process.env.REACT_APP_API_BASE_URL || '').toString().replace(/\/+$/, '');
    if (!base) return str;
    const path = str.startsWith('/') ? str : `/${str}`;
    return `${base}${path}`;
  };

  const renderCard = (user, isRoot = false) => {
    const id = String(user.id);
    const children = childrenBySupervisorId.get(id) || [];
    const isExpanded = expanded.has(id);
    const profilePic = toAbsoluteUrl(user?.profile_pic_url);
    const canShowImg = Boolean(profilePic) && !imgFailed.has(id);
    const dim = queryLower && !isMatch(user);
    const isMatched = queryLower && isMatch(user);
    const isActive = Boolean(activeMatchId) && String(activeMatchId) === id;

    return (
      <div
        className={`org-card ${isRoot ? 'org-card-root' : ''} ${isMatched ? 'org-card-match' : ''} ${isActive ? 'org-card-match-active' : ''}`}
        style={dim ? { opacity: 0.35 } : undefined}
        role="presentation"
        ref={(el) => {
          if (!el) nodeRefs.current.delete(id);
          else nodeRefs.current.set(id, el);
        }}
      >
        <button
          type="button"
          className="w-full text-left"
          onClick={() => openReporteeCourses({ user_id: user.id, user_name: user.name, user_email: user.email })}
        >
          <div className="org-card-inner">
            <div className={`org-avatar ${isRoot ? 'org-avatar-root' : ''}`}>
              {canShowImg ? (
                <img
                  src={profilePic}
                  alt={user.name}
                  className="org-avatar-img"
                  onError={() =>
                    setImgFailed((prev) => {
                      const next = new Set(prev);
                      next.add(id);
                      return next;
                    })
                  }
                />
              ) : (
                <div className="org-avatar-text">{getInitials(user.name)}</div>
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="org-name" title={user.name}>
                {user?.name || 'User'}
              </div>
              <div className="org-meta" title={user.email}>
                {user?.email || ''}
              </div>
              {renderProgress(user.id)}
            </div>
          </div>
        </button>

        {children.length > 0 && (
          <div className="org-card-footer">
            <button
              type="button"
              className="org-toggle"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(id);
              }}
            >
              <span className="org-count">{children.length}</span>
              <span className="org-toggle-text">Direct reports</span>
              <Icon icon={isExpanded ? 'fluent:chevron-up-24-regular' : 'fluent:chevron-down-24-regular'} width={18} />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderNode = (user) => {
    const id = String(user.id);
    const children = childrenBySupervisorId.get(id) || [];
    const isExpanded = expanded.has(id);

    return (
      <li key={id}>
        {renderCard(user, false)}
        {children.length > 0 && isExpanded && (
          <ul>
            {children.map((child) => renderNode(child))}
          </ul>
        )}
      </li>
    );
  };

  const renderStatus = (value) => {
    const v = (value || '').toString().toLowerCase();
    if (v === 'completed') {
      return <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-black">Completed</span>;
    }
    if (v === 'in_progress' || v === 'in progress' || v === 'inprogress') {
      return <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-black">In Progress</span>;
    }
    if (v === 'not_started' || v === 'not started' || v === 'notstarted') {
      return <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-black">Not Started</span>;
    }
    return <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-black">-</span>;
  };

  const openReporteeCourses = async (row) => {
    const userId = row?.user_id ?? row?.id;
    if (!userId) return;
    setCourseModalOpen(true);
    setCourseModalLoading(true);
    setCourseModalUser({ user_id: userId, user_name: row?.user_name ?? row?.name ?? '', user_email: row?.user_email ?? row?.email ?? '' });
    setCourseModalCourses([]);
    try {
      const res = await getReporteeCourseProgress({ user_id: userId });
      if (res?.success) {
        const list = Array.isArray(res?.data?.courses) ? res.data.courses : [];
        const user = res?.data?.user;
        if (user) {
          setCourseModalUser(user);
        }
        setCourseModalCourses(list);
      } else {
        toast.error(res?.message || res?.error_msg || 'Failed to load courses');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to load courses');
    } finally {
      setCourseModalLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-start gap-4">
        <button
          type="button"
          onClick={() => {
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate('/user/dashboard');
            }
          }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-white border border-gray-100 text-gray-700 hover:bg-gray-50 text-sm font-black shadow-sm shrink-0"
        >
          <Icon icon="fluent:arrow-left-24-regular" width={18} className="text-[#7083FF]" />
          Back
        </button>
        <div>
          <h2 className="text-lg font-black text-gray-900">Team  Progress</h2>
          <p className="text-sm text-gray-500 font-medium">View your reportees’ overall course enrollment and completion. Click a reportee for full details.</p>
        </div>
      </div>

      <div className="rounded-[20px] border border-gray-100 bg-white shadow-sm overflow-hidden">
        <div className="p-4 md:p-5 border-b border-gray-100">
          <div className="flex flex-col md:flex-row md:items-end gap-3 md:gap-4">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">Search user</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Icon icon="fluent:search-24-regular" width={18} />
                </div>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name or email"
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
                />
              </div>
            </div>

            {queryLower ? (
              <div className="flex items-center gap-2">
                <div className="px-3 py-2 rounded-2xl bg-gray-50 border border-gray-100 text-xs font-black text-gray-700 tabular-nums">
                  {matchedUserIds.length} match{matchedUserIds.length === 1 ? '' : 'es'}
                </div>
                <button
                  type="button"
                  disabled={matchedUserIds.length === 0}
                  onClick={() => {
                    if (!matchedUserIds.length) return;
                    setActiveMatchIndex((i) => {
                      const next = (i - 1 + matchedUserIds.length) % matchedUserIds.length;
                      return next;
                    });
                  }}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-white border border-gray-100 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Previous match"
                >
                  <Icon icon="fluent:chevron-left-24-regular" width={18} />
                </button>
                <button
                  type="button"
                  disabled={matchedUserIds.length === 0}
                  onClick={() => {
                    if (!matchedUserIds.length) return;
                    setActiveMatchIndex((i) => {
                      const next = (i + 1) % matchedUserIds.length;
                      return next;
                    });
                  }}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-white border border-gray-100 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Next match"
                >
                  <Icon icon="fluent:chevron-right-24-regular" width={18} />
                </button>
              </div>
            ) : null}

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={zoomOut}
                className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-white border border-gray-100 text-gray-600 hover:bg-gray-50"
                title="Zoom out"
              >
                <Icon icon="fluent:subtract-24-regular" width={18} />
              </button>
              <div className="px-3 py-2 rounded-2xl bg-gray-50 border border-gray-100 text-xs font-black text-gray-700 tabular-nums" title="Zoom">
                {Math.round(zoom * 100)}%
              </div>
              <button
                type="button"
                onClick={zoomIn}
                className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-white border border-gray-100 text-gray-600 hover:bg-gray-50"
                title="Zoom in"
              >
                <Icon icon="fluent:add-24-regular" width={18} />
              </button>
              <button
                type="button"
                onClick={resetView}
                className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-white border border-gray-100 text-gray-600 hover:bg-gray-50"
                title="Reset view"
              >
                <Icon icon="fluent:arrow-counterclockwise-24-regular" width={18} />
              </button>
            </div>
          </div>
        </div>

        {usersLoading || summaryLoading ? (
          <div className="py-16 flex items-center justify-center">
            <Loader />
          </div>
        ) : roots.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm font-medium text-gray-500">No reportees</div>
        ) : (
          <div className="p-4 md:p-5">
            <div className="org-wrap">
              <div className="org-scroll">
                <div
                  className={`org-pan ${isPanning ? 'org-pan-active' : ''}`}
                  onMouseDown={onMouseDown}
                  onMouseMove={onMouseMove}
                  onMouseUp={onMouseUp}
                  onMouseLeave={onMouseUp}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onPointerCancel={onPointerUp}
                  role="presentation"
                  title="Drag to pan"
                  ref={panRef}
                  style={{ touchAction: 'none' }}
                >
                  <div
                    className="org-stage"
                    style={{
                      transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                      transformOrigin: 'top center',
                    }}
                  >
                    <ul className="org-tree">
                      {roots.map((root) => (
                        <li key={String(root.id)}>
                          {renderCard(root, true)}
                          {(childrenBySupervisorId.get(String(root.id)) || []).length > 0 && expanded.has(String(root.id)) && (
                            <ul>
                              {(childrenBySupervisorId.get(String(root.id)) || []).map((child) => renderNode(child))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {courseModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setCourseModalOpen(false)} />
          <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-black text-gray-900">{courseModalUser?.user_name || 'User'} — Courses</div>
                <div className="text-xs font-medium text-gray-500 mt-0.5">{courseModalUser?.user_email || ''}</div>
              </div>
              <button
                type="button"
                onClick={() => setCourseModalOpen(false)}
                className="p-2 rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-200"
                aria-label="Close"
              >
                <Icon icon="fluent:dismiss-24-regular" width={18} />
              </button>
            </div>

            {courseModalLoading ? (
              <div className="py-14 flex items-center justify-center">
                <Loader />
              </div>
            ) : (
              <div className="overflow-auto max-h-[70vh]">
                <table className="min-w-[720px] w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-5 py-3 text-xs font-black text-gray-700">Course</th>
                      <th className="text-left px-5 py-3 text-xs font-black text-gray-700">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courseModalCourses.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="px-5 py-10 text-center text-sm font-medium text-gray-500">
                          No results
                        </td>
                      </tr>
                    ) : (
                      courseModalCourses.map((c, idx) => {
                        const cid = c?.course_id ?? c?.id ?? idx;
                        const cname = c?.course_name ?? c?.name ?? c?.title ?? '';
                        return (
                          <tr key={cid} className="border-b border-gray-50 hover:bg-gray-50/50">
                            <td className="px-5 py-3">
                              <div className="font-black text-gray-900 truncate max-w-[520px]">{cname || 'Course'}</div>
                            </td>
                            <td className="px-5 py-3">{renderStatus(c?.status)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamCourseProgress;
